import React, { useCallback, useState } from 'react'
import {
	Icons,
	Modal,
	ModalSection,
	Row,
	SearchInput,
	Typography,
	PrimaryButton,
} from '@copyrightagent/basic-components'
import { blue } from '../../../../lib/Colors'
import { Button } from '@material-ui/core'
import { useDropzone } from 'react-dropzone'
import globalUseStyle from '../style'
import useStyle from './UploadCertificateModalStyle'
import { uploadRegistrationCertificate } from '../api'
import { toast } from '../../../../store/utils'
import { useRegistrationCertificatesStore } from '../stores/registrationCertificateStore'
import Response from 'lib/http/Response'

export const UploadCertificateModal = ({
	uploadSuccessCallback,
}: {
	uploadSuccessCallback: (response: Response) => void
}) => {
	const {
		fetchRegistrationCertificates,
		setUploadCertificateModalOpen,
		uploadCertificateModalOpen,
	} = useRegistrationCertificatesStore()

	const globalClasses = globalUseStyle()
	const classes = useStyle()

	const [uploading, setUploading] = useState(false)
	const [registrationNumber, setRegistrationNumber] = useState('')
	const [file, setFile] = useState<File | null>(null)

	const handleClose = () => {
		setRegistrationNumber('')
		setUploadCertificateModalOpen(false)
		setUploading(false)
		setEffectiveDateCouldNotBeFound(false)
		setEffectiveDate('')
		setFile(null)
	}

	const onDrop = useCallback((acceptedFile) => {
		setFile(acceptedFile[0])
	}, [])

	const { getRootProps, getInputProps } = useDropzone({
		accept: 'application/pdf',
		onDrop,
	})

	const [effectiveDateCouldNotBeFound, setEffectiveDateCouldNotBeFound] =
		useState<Boolean>(false)
	const [effectiveDate, setEffectiveDate] = useState<string>('')

	const handleSubmit = async () => {
		setUploading(true)
		const response = await uploadRegistrationCertificate({
			registrationCertificateNumber: registrationNumber,
			file,
			effectiveDate,
		})
		setUploading(false)

		if (response.isSuccessful) {
			handleClose()
			fetchRegistrationCertificates({ isInitialLoad: true })
			uploadSuccessCallback(response)
			await toast('Registration certificate uploaded successfully', 'success')
			return
		}

		const apiErrorMessage = response.data.message

		if (
			response.status === 400 &&
			apiErrorMessage === 'Invalid effective date format'
		) {
			await toast('Invalid effective date format', 'error')
			return
		}

		if (response.status === 404) {
			setEffectiveDateCouldNotBeFound(true)
			await toast(
				'Could not find the certificate in the USCO. Manually add effective registration date and upload certificate.',
				'error',
			)
			return
		}

		await toast('Something went wrong. Please try again later', 'error')
	}

	return (
		<Modal
			open={uploadCertificateModalOpen}
			onClose={handleClose}
			title={'Upload Registration Certificates'}
			titleText={
				'Upload your registration certificates by inputting the registration certificate number and uploading the certificate in .PDF format.'
			}
			size="large"
		>
			<ModalSection>
				<Typography variant="body2">Registration Certificate number</Typography>
				<div className={classes.uploadInput}>
					<SearchInput
						onChange={(e) => setRegistrationNumber(e.target.value)}
						placeholder={'Registration Certificate Number'}
					/>
				</div>
				{effectiveDateCouldNotBeFound && (
					<>
						<Typography variant="body2">
							Registration Certificate Effective Date (YYYY-MM-DD)
						</Typography>
						<div className={classes.uploadInput}>
							<SearchInput
								onChange={(e) => setEffectiveDate(e.target.value)}
								placeholder={'Registration Certificate Effective Date'}
							/>
						</div>
					</>
				)}
				<div {...getRootProps({ className: classes.uploadDropzone })}>
					<input {...getInputProps()} />
					<Icons.UploadIcon color={blue} />
					{file ? (
						<>
							<br />
							<Typography fontColor={blue} variant="body2">
								File selected
							</Typography>
							<br />
							<Typography fontColor={blue} variant="body2">
								{file.name}
							</Typography>
						</>
					) : (
						<>
							<br />
							<Typography fontColor={blue} variant="body2">
								Drop file here
							</Typography>
							<br />
							<Typography fontColor={blue} variant="body2">
								Supports: .PDF
							</Typography>
						</>
					)}
				</div>
			</ModalSection>
			<ModalSection>
				<Row justify="flex-end">
					<Button
						className={globalClasses.invisibleButton}
						onClick={handleClose}
					>
						<Typography>Cancel</Typography>
					</Button>
					<PrimaryButton
						type="submit"
						loading={uploading}
						text={'Upload Certificate'}
						loadingText={'Uploading Certificate'}
						onClick={handleSubmit}
						disabled={uploading || !registrationNumber || !file}
					/>
				</Row>
			</ModalSection>
		</Modal>
	)
}
